import type { NotificationItem } from './types';

/**
 * Relative "time ago" label (min / h / d / w) — ported from the web app's
 * `formatWhen`.
 */
export function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  return `${w}w`;
}

export type DateBucket =
  | 'Today'
  | 'Yesterday'
  | 'This Week'
  | 'Last Week'
  | 'This Month'
  | 'Last Month'
  | 'Older';

const BUCKET_ORDER: DateBucket[] = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Last Month',
  'Older',
];

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function bucketFor(iso: string, now: Date): DateBucket {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Older';
  const today = startOfDay(now);
  const dayMs = 86_400_000;
  if (t >= today) return 'Today';
  if (t >= today - dayMs) return 'Yesterday';
  if (t >= today - 6 * dayMs) return 'This Week';
  if (t >= today - 13 * dayMs) return 'Last Week';
  if (t >= today - 29 * dayMs) return 'This Month';
  if (t >= today - 59 * dayMs) return 'Last Month';
  return 'Older';
}

export interface NotificationGroup {
  bucket: DateBucket;
  items: NotificationItem[];
}

/**
 * Group notifications into date buckets (preserving the incoming newest-first
 * order within each bucket), ready to render as labelled sections.
 */
export function groupByDate(items: NotificationItem[], now: Date = new Date()): NotificationGroup[] {
  const map = new Map<DateBucket, NotificationItem[]>();
  for (const item of items) {
    const b = bucketFor(item.createdAt, now);
    const arr = map.get(b) ?? [];
    arr.push(item);
    map.set(b, arr);
  }
  return BUCKET_ORDER.filter((b) => map.has(b)).map((bucket) => ({
    bucket,
    items: map.get(bucket) as NotificationItem[],
  }));
}
